/**
 * GST Calculation Utilities
 * Handles CGST/SGST/IGST calculations based on state codes
 */

import { roundForCalculation, roundPercentage } from './mathUtils';

/**
 * Extract state code from GST number (first 2 digits)
 * @param {string} gstNumber - GST number
 * @returns {string|null} - State code or null if invalid
 */
export const extractStateCode = (gstNumber) => {
  if (!gstNumber || typeof gstNumber !== 'string' || gstNumber.length < 2) {
    return null;
  }
  return gstNumber.substring(0, 2);
};

/**
 * Get seller state code from company settings
 * @param {object} companySettings - Company settings object
 * @returns {string|null} - Seller state code
 */
export const getSellerStateCode = (companySettings) => {
  const sellerGST = companySettings?.gstNumber;
  return extractStateCode(sellerGST);
};


// State Code to Name Mapping
export const GST_STATE_CODES = {
  '01': 'Jammu & Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '25': 'Daman & Diu',
  '26': 'Dadra & Nagar Haveli',
  '27': 'Maharashtra',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory'
};

/**
 * Get State Name from GST Number or Code
 * @param {string} codeOrGst - 2 digit code or full GST number
 * @returns {string} - State Name or ''
 */
export const getStateNameFromGST = (codeOrGst) => {
  if (!codeOrGst) return '';
  const code = codeOrGst.length > 2 ? codeOrGst.substring(0, 2) : codeOrGst;
  return GST_STATE_CODES[code] || '';
};

/**
 * Determine GST type based on buyer and seller state codes/names
 * @param {string} buyerGST - Buyer GST number
 * @param {string} sellerStateCode - Seller state code (e.g. '36' for Telangana)
 * @param {string} buyerStateName - Buyer state name (optional, used if GST is missing)
 * @returns {object} - GST type information
 */
export const determineGSTType = (buyerGST, sellerStateCode, buyerStateName = '') => {
  let buyerStateCode = null;

  // 1. Try to get state code from Buyer GST
  if (buyerGST && buyerGST.length >= 2) {
    buyerStateCode = extractStateCode(buyerGST);
  }

  // 2. If no GST, try to infer from State Name if provided
  if (!buyerStateCode && buyerStateName) {
    // Reverse lookup state code from name (simple case-insensitive match)
    const normalizedStateName = buyerStateName.trim().toLowerCase();
    const entry = Object.entries(GST_STATE_CODES).find(([code, name]) =>
      name.toLowerCase() === normalizedStateName
    );
    if (entry) {
      buyerStateCode = entry[0];
    }
  }

  // 3. Fallback: If we still don't have buyer state code
  // If we have a buyerStateName but couldn't map it to a code, we can compare names directly if seller has a name
  // BUT sellerStateCode is a code. Let's get seller name.
  const sellerStateName = GST_STATE_CODES[sellerStateCode] || '';

  if (!buyerStateCode) {
    if (buyerStateName && sellerStateName) {
      // Compare names directly
      const isSameState = buyerStateName.trim().toLowerCase() === sellerStateName.toLowerCase();
      return {
        type: isSameState ? 'CGST_SGST' : 'IGST',
        isSameState,
        buyerStateCode: isSameState ? sellerStateCode : '99', // '99' as placeholder for diff state
        sellerStateCode
      };
    }

    // Default to CGST_SGST (Same State) if insufficient info matches existing behavior
    return {
      type: 'CGST_SGST',
      isSameState: true,
      buyerStateCode: sellerStateCode,
      sellerStateCode
    };
  }

  // Compare state codes
  const isSameState = buyerStateCode === sellerStateCode;

  return {
    type: isSameState ? 'CGST_SGST' : 'IGST',
    isSameState,
    buyerStateCode,
    sellerStateCode
  };
};

/**
 * Calculate GST breakdown for an item
 * @param {number} amount - Taxable amount
 * @param {number} gstPercentage - GST percentage
 * @param {string} gstType - 'CGST_SGST' or 'IGST'
 * @returns {object} - GST breakdown
 */
export const calculateGSTBreakdown = (amount, gstPercentage, gstType) => {
  const taxableAmount = parseFloat(amount) || 0;
  const gstRate = parseFloat(gstPercentage) || 0;

  if (gstType === 'CGST_SGST') {
    const cgstRate = gstRate / 2;
    const sgstRate = gstRate / 2;
    const cgstAmount = roundForCalculation((taxableAmount * cgstRate) / 100);
    const sgstAmount = roundForCalculation((taxableAmount * sgstRate) / 100);
    const totalGST = roundForCalculation(cgstAmount + sgstAmount);

    return {
      type: 'CGST_SGST',
      cgstPercentage: roundPercentage(cgstRate),
      cgstAmount: cgstAmount,
      sgstPercentage: roundPercentage(sgstRate),
      sgstAmount: sgstAmount,
      igstPercentage: 0,
      igstAmount: 0,
      totalGST: totalGST
    };
  } else {
    const igstAmount = roundForCalculation((taxableAmount * gstRate) / 100);

    return {
      type: 'IGST',
      cgstPercentage: 0,
      cgstAmount: 0,
      sgstPercentage: 0,
      sgstAmount: 0,
      igstPercentage: roundPercentage(gstRate),
      igstAmount: igstAmount,
      totalGST: igstAmount
    };
  }
};

/**
 * Calculate total GST breakdown for multiple items
 * @param {array} items - Array of items
 * @param {string} buyerGST - Buyer GST number
 * @param {string} buyerGST - Buyer GST number
 * @param {object} companySettings - Company settings
 * @param {string} buyerStateName - Buyer state name (optional)
 * @returns {object} - Total GST breakdown
 */
export const calculateTotalGSTBreakdown = (items, buyerGST, companySettings, buyerStateName = '') => {
  const sellerStateCode = getSellerStateCode(companySettings);
  const gstTypeInfo = determineGSTType(buyerGST, sellerStateCode, buyerStateName);

  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  let totalGST = 0;
  let totalTaxableAmount = 0;

  items.forEach(item => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount) || 0;
    const gstRate = parseFloat(item.gst_rate) || 0;

    // Calculate taxable amount with rounding
    const baseAmount = roundForCalculation(quantity * unitPrice);
    const discountAmount = roundForCalculation((baseAmount * discount) / 100);
    const taxableAmount = roundForCalculation(baseAmount - discountAmount);

    totalTaxableAmount = roundForCalculation(totalTaxableAmount + taxableAmount);

    // Calculate GST breakdown for this item
    const gstBreakdown = calculateGSTBreakdown(taxableAmount, gstRate, gstTypeInfo.type);

    totalCGST = roundForCalculation(totalCGST + gstBreakdown.cgstAmount);
    totalSGST = roundForCalculation(totalSGST + gstBreakdown.sgstAmount);
    totalIGST = roundForCalculation(totalIGST + gstBreakdown.igstAmount);
    totalGST = roundForCalculation(totalGST + gstBreakdown.totalGST);
  });

  const nonZeroGstRates = [...new Set(items.map(item => parseFloat(item.gst_rate) || 0).filter(rate => rate > 0))].sort((a, b) => a - b);
  const isMultipleRates = nonZeroGstRates.length > 1;
  const uniformGstRate = isMultipleRates ? 0 : (nonZeroGstRates[0] || 0);

  const cgstPercentageStr = isMultipleRates ? nonZeroGstRates.map(r => roundPercentage(r / 2)).join(', ') : roundPercentage(uniformGstRate / 2);
  const igstPercentageStr = isMultipleRates ? nonZeroGstRates.map(r => roundPercentage(r)).join(', ') : roundPercentage(uniformGstRate);

  return {
    type: gstTypeInfo.type,
    isSameState: gstTypeInfo.isSameState,
    buyerStateCode: gstTypeInfo.buyerStateCode,
    sellerStateCode: gstTypeInfo.sellerStateCode,
    totalTaxableAmount: totalTaxableAmount,
    cgstAmount: totalCGST,
    sgstAmount: totalSGST,
    igstAmount: totalIGST,
    totalGST: totalGST,
    // Return comma-separated rates if multiple, otherwise return the uniform rate
    cgstPercentage: gstTypeInfo.type === 'CGST_SGST' ? cgstPercentageStr : 0,
    sgstPercentage: gstTypeInfo.type === 'CGST_SGST' ? cgstPercentageStr : 0,
    igstPercentage: gstTypeInfo.type === 'IGST' ? igstPercentageStr : 0
  };
};

/**
 * Format GST breakdown for display
 * @param {object} gstBreakdown - GST breakdown object
 * @returns {object} - Formatted display data
 */
export const formatGSTBreakdownForDisplay = (gstBreakdown) => {
  const { type, cgstAmount, sgstAmount, igstAmount, totalGST, cgstPercentage, sgstPercentage, igstPercentage } = gstBreakdown;

  const getLabel = (name, percentage) => {
    if (typeof percentage === 'string' && percentage.includes(',')) {
      return name;
    }
    return `${name} (${percentage}%)`;
  };

  if (type === 'CGST_SGST') {
    return {
      type: 'CGST_SGST',
      lines: [
        {
          label: getLabel('CGST', cgstPercentage),
          amount: cgstAmount,
          formatted: `₹${cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
        },
        {
          label: getLabel('SGST', sgstPercentage),
          amount: sgstAmount,
          formatted: `₹${sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
        }
      ],
      total: {
        label: 'Total GST',
        amount: totalGST,
        formatted: `₹${totalGST.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
      }
    };
  } else {
    return {
      type: 'IGST',
      lines: [
        {
          label: getLabel('IGST', igstPercentage),
          amount: igstAmount,
          formatted: `₹${igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
        }
      ],
      total: {
        label: 'Total GST',
        amount: totalGST,
        formatted: `₹${totalGST.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
      }
    };
  }
};
